import { Resource } from "@domain/base/Resource";
import { IShopifyConnectorRepository } from "@domain/connection/IMarketplaceRepository";
import { ShopifyConnectorDataSource } from "../datasources/ShopifyConnectorDataSource";
import { FullSyncJob } from "@domain/connection/models/ImportJob";
import { ShopifySettings, ShopifyProductMatchMapping } from "@domain/connection/models/ConnectionSettings";
import {
    CompleteShopifySetupResponse,
    FinishShopifySetupInput,
    ShopifyMatchPreviewResponse,
} from "@domain/connection/models/ShopifyMatchPreview";

export class ShopifyConnectorRepository implements IShopifyConnectorRepository {

    constructor(private dataSource: ShopifyConnectorDataSource) { }

    async initiateShopifyAuth(shop: string, redirectUri?: string): Promise<Resource<{ authUrl: string }>> {
        try {
            const data = await this.dataSource.initiateShopifyAuth(shop, redirectUri);
            return {
                status: 'success',
                data
            };
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: message || 'Failed to initiate Shopify auth'
                }
            };
        }
    }

    async reconnect(connectionId: string, redirectUri?: string): Promise<Resource<{ authUrl: string }>> {
        try {
            const data = await this.dataSource.reconnect(connectionId, redirectUri);
            return { status: 'success', data };
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: message || 'Failed to reconnect Shopify',
                },
            };
        }
    }

    async initiateInstall(redirectUri?: string): Promise<Resource<{ installUrl: string }>> {
        try {
            const data = await this.dataSource.initiateInstall(redirectUri);
            return { status: 'success', data };
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: message || 'Failed to start Shopify install',
                },
            };
        }
    }

    async claimShopifyInstall(shop: string, installToken: string): Promise<Resource<{ connectionId: string }>> {
        try {
            const data = await this.dataSource.claimShopifyInstall(shop, installToken);
            return { status: 'success', data };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to claim Shopify install'
                }
            };
        }
    }

    async syncFull(connectionId: string): Promise<Resource<FullSyncJob[]>> {
        try {
            const data = await this.dataSource.syncFull(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to start full sync'
                }
            };
        }
    }

    async syncProducts(connectionId: string): Promise<Resource<void>> {
        try {
            await this.dataSource.syncProducts(connectionId);
            return { status: 'success', data: undefined };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync products' } };
        }
    }

    async syncOrders(connectionId: string): Promise<Resource<void>> {
        try {
            await this.dataSource.syncOrders(connectionId);
            return { status: 'success', data: undefined };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync orders' } };
        }
    }

    async syncInventory(connectionId: string): Promise<Resource<void>> {
        try {
            await this.dataSource.syncInventory(connectionId);
            return { status: 'success', data: undefined };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync inventory' } };
        }
    }

    async getSettings(connectionId: string): Promise<Resource<ShopifySettings>> {
        try {
            const data = await this.dataSource.getSettings(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to load settings' } };
        }
    }

    async updateSettings(connectionId: string, settings: Partial<ShopifySettings>): Promise<Resource<ShopifySettings>> {
        try {
            const data = await this.dataSource.updateSettings(connectionId, settings);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to save settings' } };
        }
    }

    async getMatchPreview(
        connectionId: string,
        mapping?: Partial<ShopifyProductMatchMapping>,
        pageToken?: string,
    ): Promise<Resource<ShopifyMatchPreviewResponse>> {
        try {
            const data = await this.dataSource.getMatchPreview(connectionId, mapping, pageToken);
            return { status: 'success', data };
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            return {
                status: 'error',
                error: { type: 'UnknownError', message: message || 'Failed to load match preview' },
            };
        }
    }

    async completeSetup(
        connectionId: string,
        productMatchMapping: ShopifyProductMatchMapping,
    ): Promise<Resource<CompleteShopifySetupResponse>> {
        try {
            const data = await this.dataSource.completeSetup(connectionId, productMatchMapping);
            return { status: 'success', data };
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            return {
                status: 'error',
                error: { type: 'UnknownError', message: message || 'Failed to complete setup' },
            };
        }
    }

    async finishSetup(
        connectionId: string,
        input: FinishShopifySetupInput,
    ): Promise<Resource<CompleteShopifySetupResponse>> {
        try {
            const data = await this.dataSource.finishSetup(connectionId, input);
            return { status: 'success', data };
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            return {
                status: 'error',
                error: { type: 'UnknownError', message: message || 'Failed to finish setup' },
            };
        }
    }
}
